import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOURCE = process.env.REACT_APP_PRODUCT_SOURCE || "api";

const SHOPIFY_DOMAIN = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN;
const SHOPIFY_TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;
const SHOPIFY_API_VERSION = "2026-01";

const PRODUCTS_QUERY = `
  query Products($first: Int!, $query: String) {
    products(first: $first, query: $query) {
      edges {
        node {
          handle
          title
          productType
          createdAt
          tags
          description
          priceRange { minVariantPrice { amount } }
          compareAtPriceRange { minVariantPrice { amount } }
          featuredImage { url altText }
          images(first: 4) { edges { node { url altText } } }
          options { name values }
          variants(first: 100) { edges { node { id availableForSale price { amount } image { url } selectedOptions { name value } } } }
          media(first: 8) { edges { node { mediaContentType ... on Video { sources { url mimeType } } } } }
        }
      }
    }
  }
`;

function fromShopify(node) {
    const images = node.images.edges.map((e) => e.node.url);
    const price = Math.round(parseFloat(node.priceRange.minVariantPrice.amount));
    const sizes = node.options.find((o) => o.name.toLowerCase() === "size")?.values || ["XS", "S", "M", "L", "XL", "XXL"];
    const colors = node.options.find((o) => o.name.toLowerCase() === "color")?.values || ["Onyx"];
    const videoNode = (node.media?.edges || []).find((e) => e.node.mediaContentType === "VIDEO")?.node;
    const video = videoNode?.sources?.find((s) => s.mimeType === "video/mp4")?.url || videoNode?.sources?.[0]?.url || null;
    const deptTag = node.tags.find((t) => /^dept:/i.test(t));
    const deptTags = node.tags.filter((t) => /^dept:/i.test(t)).map((t) => t.slice(5));
    const variants = node.variants.edges.map((e) => {
        const opt = (n) => e.node.selectedOptions.find((o) => o.name.toLowerCase() === n)?.value;
        const isRaised = e.node.selectedOptions.some((o) => /raised/i.test(o.value));
        return {
            id: e.node.id,
            available: e.node.availableForSale,
            size: opt("size"),
            color: opt("color"),
            print: isRaised ? "RAISED" : "STANDARD",
            price: Math.round(parseFloat(e.node.price.amount)),
            image: e.node.image?.url || null,
        };
    });
    const raisedVariants = variants.filter((v) => v.print === "RAISED");
    const hasRaised = raisedVariants.length > 0;
    const raisedMin = hasRaised ? Math.min(...raisedVariants.map((v) => v.price)) : null;
    const raisedImage = raisedVariants.find((v) => v.image)?.image || null;
    return {
        id: node.handle,
        name: node.title.toUpperCase(),
        category: node.productType || "T-Shirts",
        department: deptTag ? deptTag.slice(5) : "Men",
        departments: deptTags.length ? deptTags : ["Men"],
        collection: node.tags.find((t) => ["Drop 001", "Snake Edition", "Raised Reality", "Essentials"].includes(t)) || "Essentials",
        price,
        hasRaised,
        raised_price: raisedMin,
        raisedSurcharge: hasRaised ? raisedMin - price : 0,
        raisedImage,
        colors,
        sizes,
        variants,
        tag: node.tags.find((t) => t.startsWith("badge:"))?.replace("badge:", "") || null,
        front: node.featuredImage?.url || images[0],
        back: images[1] || images[0],
        video,
        createdAt: node.createdAt || null,
        description: node.description,
        details: [],
        material: "",
        fit: "",
        care: "",
    };
}

async function shopifyFetch(query, variables) {
    const r = await axios.post(
        `https://${SHOPIFY_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`,
        { query, variables },
        { headers: { "X-Shopify-Storefront-Access-Token": SHOPIFY_TOKEN, "Content-Type": "application/json" } }
    );
    return r.data.data;
}

export async function fetchProducts(params = {}) {
    if (SOURCE === "shopify") {
        const queryParts = [];
        if (params.category) queryParts.push(`product_type:${params.category}`);
        if (params.q) queryParts.push(`title:*${params.q}*`);
        if (params.collection) queryParts.push(`tag:${params.collection}`);
        const data = await shopifyFetch(PRODUCTS_QUERY, { first: 60, query: queryParts.join(" AND ") || null });
        let items = data.products.edges.map((e) => fromShopify(e.node));
        if (params.department) items = items.filter((p) => (p.departments || [p.department]).includes(params.department));
        if (params.sort === "price-asc") items.sort((a, b) => a.price - b.price);
        if (params.sort === "price-desc") items.sort((a, b) => b.price - a.price);
        if (params.sort === "name") items.sort((a, b) => a.name.localeCompare(b.name));
        if (params.size) items = items.filter((p) => p.sizes.includes(params.size));
        if (params.color) items = items.filter((p) => p.colors.includes(params.color));
        return items;
    }
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    const r = await axios.get(`${API}/products${qs ? `?${qs}` : ""}`);
    return r.data;
}

export async function fetchProduct(id) {
    if (SOURCE === "shopify") {
        const items = await fetchProducts({});
        const found = items.find((p) => p.id === id);
        if (!found) throw new Error("not found");
        return found;
    }
    const r = await axios.get(`${API}/products/${id}`);
    return r.data;
}

const CART_CREATE_MUTATION = `
  mutation CartCreate($lines: [CartLineInput!]!) {
    cartCreate(input: { lines: $lines }) {
      cart { id checkoutUrl }
      userErrors { field message }
    }
  }
`;

export async function shopifyCheckout(items) {
    const lines = items.filter((i) => i.variant_id).map((i) => ({ merchandiseId: i.variant_id, quantity: i.qty }));
    if (!lines.length) throw new Error("Cart items are not linked to Shopify variants");
    const data = await shopifyFetch(CART_CREATE_MUTATION, { lines });
    if (data.cartCreate.userErrors.length) throw new Error(data.cartCreate.userErrors[0].message);
    // The primary domain now serves this headless site — route checkout to the myshopify domain
    return data.cartCreate.cart.checkoutUrl.replace(/^https:\/\/vantageownersclub\.com/, `https://${SHOPIFY_DOMAIN}`);
}

export const PRODUCT_SOURCE = SOURCE;
